import React, { useState, useCallback, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import moment from "moment";

import CustomButton from "components/common/Button";
import ButtonWithDropdown from "components/common/ButtonWithDropdown";
import Menu, { MenuItem } from "components/common/Menu";
import SelectShiftModal from "components/CheckInCheckOut/SelectShiftModal";
import BreakTimeExceededModal from "components/CheckInCheckOut/BreakTimeExceededModal";
import EndDayModal from "components/CheckInCheckOut/EndDayModal/EndDayModal";
import ClockOutSuccessModal from "components/CheckInCheckOut/ClockOutSuccessModal/ClockOutSuccessModal";
import MissedClockOutModal from "components/CheckInCheckOut/MissedClockOutModal/MissedClockOutModal";
import { AlertType } from "components/common/Snackbar";

import { clockEntry, recordMissedEntry, getTimesheetTicker } from "services/CheckInCheckOut/CheckInAndBreaks";
import { showSnackbar } from "store/actions/Utility";

import { ReactComponent as BurgerIcon } from "assets/images/break-logo.svg";
import { ReactComponent as FeederIcon } from "assets/images/nursing-logo.svg";
import { ReactComponent as WorkIcon } from "assets/images/work-logo.svg";

import "./ClockInCardV2.scss";

function ClockInCardV2({
  tickerData,
  onShiftConfirm,
  onAttendanceUpdate,
  onRefreshTicker,
  setTickerData,
  hasSchedule,
  timeoffCountry,
}) {
  const navigate = useNavigate();
  const reduxDispatch = useDispatch();

  const [openSelectShift, setOpenSelectShift] = useState(false);
  const [openBreakExceededModal, setOpenBreakExceededModal] = useState(false);
  const [openEndDayModal, setOpenEndDayModal] = useState(false);
  const [openClockOutSuccessModal, setOpenClockOutSuccessModal] = useState(false);
  const [openMissedClockOutModal, setOpenMissedClockOutModal] = useState(false);
  const [cachedDaySummary, setCachedDaySummary] = useState(null);
  const [isLoadingModalData, setIsLoadingModalData] = useState(false);
  const [modalTickerData, setModalTickerData] = useState(null);
  const [isClockInOutLoading, setIsClockInOutLoading] = useState(false);
  const [isBreakLoading, setIsBreakLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [elapsedMilliseconds, setElapsedMilliseconds] = useState(0);
  const [breakElapsedMilliseconds, setBreakElapsedMilliseconds] = useState(0);

  const openBreakMenu = Boolean(anchorEl);

  const { todayStatus = {}, checkIn = {}, previousDayCheckIn = {}, employee = {} } = tickerData || {};
  const { isNursingBreakAllowed = false } = employee;
  const { isClockedIn = false, missedPreviousDayCheckout = false } = todayStatus;

  const activeCheckInData = missedPreviousDayCheckout && !isClockedIn ? previousDayCheckIn : checkIn;
  const {
    timeElapsedExcludingBreaks = 0,
    totalBreakTime = 0,
    totalNursingBreakTime = 0,
    breaks: checkInBreaks = [],
  } = activeCheckInData ?? {};

  // Get active break from checkIn.breaks array. The API treats a break as still running when its
  // outTime is null OR an empty string, so match on any falsy value — checking `=== null` alone
  // hides empty-string breaks from the widget while the server still considers them open.
  const getActiveBreak = useCallback(() => {
    if (!checkInBreaks || checkInBreaks.length === 0) return null;
    return checkInBreaks.find((breakItem) => !breakItem.outTime);
  }, [checkInBreaks]);

  const getBreakType = useCallback(() => {
    const activeBreak = getActiveBreak();
    if (!activeBreak) return null;
    return activeBreak.type === "nursing_break" ? "nursing_break" : "break";
  }, [getActiveBreak]);

  const getPreviousDayBreakWithNullOutTime = useCallback(() => {
    if (!previousDayCheckIn || !previousDayCheckIn.breaks || previousDayCheckIn.breaks.length === 0) {
      return null;
    }
    return previousDayCheckIn.breaks.find((breakItem) => !breakItem.outTime);
  }, [previousDayCheckIn]);

  const activeBreak = useMemo(() => getActiveBreak(), [getActiveBreak]);
  const isOnBreak = Boolean(activeBreak);
  const previousDayBreak = useMemo(() => getPreviousDayBreakWithNullOutTime(), [getPreviousDayBreakWithNullOutTime]);

  // Initialize elapsed time from ticker API (work time excluding breaks)
  useEffect(() => {
    if ((isClockedIn || (missedPreviousDayCheckout && !isClockedIn)) && timeElapsedExcludingBreaks) {
      setElapsedMilliseconds(timeElapsedExcludingBreaks);
    } else {
      setElapsedMilliseconds(0);
    }
  }, [isClockedIn, missedPreviousDayCheckout, timeElapsedExcludingBreaks]);

  // Initialize break elapsed time from ticker API (from active break's timeElapsed)
  useEffect(() => {
    if (isOnBreak && activeBreak?.timeElapsed) {
      setBreakElapsedMilliseconds(activeBreak.timeElapsed);
    } else {
      setBreakElapsedMilliseconds(0);
    }
  }, [isOnBreak, activeBreak]);

  // Start work time counter when clocked in and not on break
  useEffect(() => {
    if (!isClockedIn || isOnBreak || (missedPreviousDayCheckout && !isClockedIn)) {
      return undefined; // Stop work counter when on break or showing missed checkout
    }

    const interval = setInterval(() => {
      setElapsedMilliseconds((prev) => prev + 1000);
    }, 1000);

    return () => clearInterval(interval);
  }, [isClockedIn, isOnBreak, missedPreviousDayCheckout]);

  // Start break time counter when on break
  useEffect(() => {
    if (!isOnBreak) {
      return undefined; // Only run when on break
    }

    const interval = setInterval(() => {
      setBreakElapsedMilliseconds((prev) => prev + 1000);
    }, 1000);

    return () => clearInterval(interval);
  }, [isOnBreak]);

  // Fetch latest ticker data when end day modal opens
  useEffect(() => {
    const fetchModalData = async () => {
      if (openEndDayModal) {
        setIsLoadingModalData(true);
        try {
          const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser")) || {};
          const { userId: employeeId } = loggedInUser;

          if (employeeId) {
            const tickerResponse = await getTimesheetTicker(employeeId);
            setModalTickerData(tickerResponse);
          }
        } catch (error) {
          // Error fetching modal data
        } finally {
          setIsLoadingModalData(false);
        }
      }
    };

    fetchModalData();
  }, [openEndDayModal]);

  const formatSeconds = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return { hours, minutes, seconds: secs };
  };

  const getTotalElapsedTime = () => {
    if (!isClockedIn) {
      return { hours: 0, minutes: 0, seconds: 0 };
    }
    const seconds = Math.floor(elapsedMilliseconds / 1000);
    return formatSeconds(seconds);
  };

  const getWorkTime = () => {
    if (!isClockedIn || !timeElapsedExcludingBreaks) {
      return { hours: 0, minutes: 0, seconds: 0 };
    }
    const seconds = Math.floor(timeElapsedExcludingBreaks / 1000);
    return formatSeconds(seconds);
  };

  const getCurrentBreakTime = () => {
    const currentActiveBreak = getActiveBreak();
    if (!currentActiveBreak) {
      return { hours: 0, minutes: 0, seconds: 0 };
    }
    const seconds = Math.floor(breakElapsedMilliseconds / 1000);
    return formatSeconds(seconds);
  };

  const getTotalBreakTime = () => {
    if (!isClockedIn || !totalBreakTime) {
      return { hours: 0, minutes: 0, seconds: 0 };
    }
    const seconds = Math.floor(totalBreakTime / 1000);
    return formatSeconds(seconds);
  };

  const getTotalNursingTime = () => {
    if (!isClockedIn || !totalNursingBreakTime) {
      return { hours: 0, minutes: 0, seconds: 0 };
    }
    const seconds = Math.floor(totalNursingBreakTime / 1000);
    return formatSeconds(seconds);
  };

  const getDaySummary = (data = null) => {
    const sourceData = data || {};
    const { checkIn: checkInData } = sourceData || {};
    const {
      timeElapsedExcludingBreaks: workTimeMs = 0,
      totalBreakTime: breakTimeMs = 0,
      totalNursingBreakTime: nursingTimeMs = 0,
      timeElapsed: totalTimeMs = 0,
    } = checkInData || {};

    const hours = Math.floor(workTimeMs / 3600000);
    const minutes = Math.floor((workTimeMs % 3600000) / 60000);

    const totalHours = Math.floor(totalTimeMs / 3600000);
    const totalMinutes = Math.floor((totalTimeMs % 3600000) / 60000);

    return {
      timeWorked: `${hours}h ${minutes}m`,
      breaks: `${breakTimeMs / 60000}m`,
      nursing: `${nursingTimeMs / 60000}m`,
      totalHours: `${totalHours}h ${totalMinutes}m`,
    };
  };

  const getMissedCheckoutSummary = () => {
    if (!previousDayCheckIn) return null;

    const {
      timeElapsedExcludingBreaks: workTimeMs = 0,
      totalBreakTime: breakTimeMs = 0,
      totalNursingBreakTime: nursingTimeMs = 0,
      timeElapsed: totalTimeMs = 0,
    } = previousDayCheckIn;

    const hours = Math.floor(workTimeMs / 3600000);
    const minutes = Math.floor((workTimeMs % 3600000) / 60000);

    const totalHours = Math.floor(totalTimeMs / 3600000);
    const totalMinutes = Math.floor((totalTimeMs % 3600000) / 60000);

    return {
      timeWorked: `${hours}h ${minutes}m`,
      breaks: `${breakTimeMs / 60000}m`,
      nursing: `${nursingTimeMs / 60000}m`,
      totalHours: `${totalHours}h ${totalMinutes}m`,
    };
  };

  const formatTimeUnit = (value) => {
    return value.toString().padStart(2, "0");
  };

  const getTimerLabel = () => {
    if (missedPreviousDayCheckout && !isClockedIn) {
      return `Work Time (${previousDayCheckIn.date || "Previous Day"})`;
    }
    const currentActiveBreak = getActiveBreak();
    if (currentActiveBreak) {
      return getBreakType() === "break" ? "Break Time" : "Nursing";
    }
    return "Work Time";
  };

  const handleClockIn = async () => {
    if (!isClockedIn) {
      if (hasSchedule === false) {
        setOpenSelectShift(true);
        return;
      }

      setIsClockInOutLoading(true);
      try {
        const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser")) || {};
        const { userId: employeeId } = loggedInUser;

        if (!employeeId) {
          reduxDispatch(
            showSnackbar({ type: AlertType.error, message: "Employee ID not found", dispatch: reduxDispatch })
          );
          setIsClockInOutLoading(false);
          return;
        }

        await clockEntry(employeeId, {
          action: "check_in",
        });
        if (onRefreshTicker) {
          await onRefreshTicker();
        }

        reduxDispatch(
          showSnackbar({
            type: AlertType.success,
            message: "Successfully clocked in. Have a productive day!",
            dispatch: reduxDispatch,
          })
        );
        setIsClockInOutLoading(false);
      } catch (error) {
        reduxDispatch(showSnackbar({ type: AlertType.error, message: error.message, dispatch: reduxDispatch }));
        setIsClockInOutLoading(false);
      }
    } else {
      setOpenEndDayModal(true);
    }
  };

  const handleMissedCheckout = () => {
    setOpenMissedClockOutModal(true);
  };

  const handleSelectShiftClose = () => {
    setOpenSelectShift(false);
  };

  const handleSelectShiftConfirm = async (shiftData) => {
    setOpenSelectShift(false);

    if (onShiftConfirm) {
      onShiftConfirm(shiftData);
    }

    if (onRefreshTicker) {
      await onRefreshTicker();
    }
  };

  const handleViewTimesheet = () => {
    navigate("/main/timesheet");
  };

  const handleStartBreak = async (type = "break") => {
    const currentActiveBreak = getActiveBreak();
    const currentBreakType = getBreakType();

    if (currentActiveBreak) {
      // End break - check if there's a previous day break with null outTime
      if (previousDayBreak) {
        setOpenBreakExceededModal(true);
        return;
      }

      setIsBreakLoading(true);
      try {
        const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser")) || {};
        const { userId: employeeId } = loggedInUser;

        if (!employeeId) {
          reduxDispatch(
            showSnackbar({ type: AlertType.error, message: "Employee ID not found", dispatch: reduxDispatch })
          );
          setIsBreakLoading(false);
          return;
        }

        const action = currentBreakType === "nursing_break" ? "nursing_break_end" : "break_end";

        await clockEntry(employeeId, {
          action,
        });

        if (onRefreshTicker) {
          await onRefreshTicker();
        }

        const breakTypeName = currentBreakType === "nursing_break" ? "nursing break" : "break";
        reduxDispatch(
          showSnackbar({
            type: AlertType.success,
            message: `Successfully ended ${breakTypeName}. Back to work!`,
            dispatch: reduxDispatch,
          })
        );
        setIsBreakLoading(false);
      } catch (error) {
        setIsBreakLoading(false);
        // Backend will validate if nursing break exceeded 1 hour
        if (error.errorCode === "NURSING_BREAK_EXCEEDED") {
          setOpenBreakExceededModal(true);
        } else {
          reduxDispatch(showSnackbar({ type: AlertType.error, message: error.message, dispatch: reduxDispatch }));
        }
      }
    } else {
      // Start break
      setIsBreakLoading(true);
      try {
        const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser")) || {};
        const { userId: employeeId } = loggedInUser;

        if (!employeeId) {
          reduxDispatch(
            showSnackbar({ type: AlertType.error, message: "Employee ID not found", dispatch: reduxDispatch })
          );
          setIsBreakLoading(false);
          return;
        }

        const action = type === "nursing" ? "nursing_break_start" : "break_start";

        await clockEntry(employeeId, {
          action,
        });

        if (onRefreshTicker) {
          await onRefreshTicker();
        }

        const breakTypeName = type === "nursing" ? "nursing break" : "break";
        reduxDispatch(
          showSnackbar({
            type: AlertType.success,
            message: `Successfully started ${breakTypeName}. Enjoy your break!`,
            dispatch: reduxDispatch,
          })
        );
        setIsBreakLoading(false);
      } catch (error) {
        setIsBreakLoading(false);
        reduxDispatch(showSnackbar({ type: AlertType.error, message: error.message, dispatch: reduxDispatch }));
      }
    }
  };

  const handleBreakMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleBreakMenuClose = () => {
    setAnchorEl(null);
  };

  const handleBreakSelect = () => {
    handleStartBreak("break");
    handleBreakMenuClose();
  };

  const handleNursingBreakSelect = () => {
    handleStartBreak("nursing");
    handleBreakMenuClose();
  };

  const handleBreakExceededModalClose = () => {
    setOpenBreakExceededModal(false);
  };

  const handleEndDayModalClose = () => {
    setOpenEndDayModal(false);
    setIsLoadingModalData(false);
    setModalTickerData(null);
    setTickerData(modalTickerData);
    setCachedDaySummary(getDaySummary(modalTickerData));
  };

  const handleClockOutSuccessModalClose = () => {
    setOpenClockOutSuccessModal(false);

    // Notify parent component to refresh attendance data
    if (onAttendanceUpdate) {
      onAttendanceUpdate();
    }
  };

  const handleViewTimesheetFromSuccess = () => {
    setOpenClockOutSuccessModal(false);
    navigate("/main/timesheet");
  };

  const handleMissedClockOutModalClose = () => {
    setOpenMissedClockOutModal(false);
  };

  const handleMissedClockOutSave = async (clockOutTime) => {
    try {
      const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser")) || {};
      const { userId: employeeId } = loggedInUser;

      if (!employeeId) {
        reduxDispatch(
          showSnackbar({ type: AlertType.error, message: "Employee ID not found", dispatch: reduxDispatch })
        );
        return;
      }

      if (!clockOutTime) {
        reduxDispatch(
          showSnackbar({ type: AlertType.error, message: "Clock-out time is required", dispatch: reduxDispatch })
        );
        return;
      }

      // Format checkout time as "YYYY-MM-DD HH:mm"
      const year = clockOutTime.getFullYear();
      const month = String(clockOutTime.getMonth() + 1).padStart(2, "0");
      const day = String(clockOutTime.getDate()).padStart(2, "0");
      const hours = String(clockOutTime.getHours()).padStart(2, "0");
      const minutes = String(clockOutTime.getMinutes()).padStart(2, "0");
      const formattedEntryTime = `${year}-${month}-${day} ${hours}:${minutes}`;

      // Call missed entry API for checkout
      await recordMissedEntry(employeeId, formattedEntryTime, "checkout");

      // Close Missed Clock-Out Modal and open Success Modal
      setOpenMissedClockOutModal(false);

      const missedCheckoutSummary = getMissedCheckoutSummary();
      setCachedDaySummary(missedCheckoutSummary);
      setOpenClockOutSuccessModal(true);

      if (onRefreshTicker) {
        await onRefreshTicker();
      }
    } catch (error) {
      reduxDispatch(showSnackbar({ type: AlertType.error, message: error.message, dispatch: reduxDispatch }));
    }
  };

  const handleEndDayConfirm = async () => {
    try {
      const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser")) || {};
      const { userId: employeeId } = loggedInUser;

      if (!employeeId) {
        reduxDispatch(
          showSnackbar({ type: AlertType.error, message: "Employee ID not found", dispatch: reduxDispatch })
        );
        return;
      }

      // Capture day summary BEFORE clocking out
      const summaryBeforeClockOut = getDaySummary();
      setCachedDaySummary(summaryBeforeClockOut);

      await clockEntry(employeeId, {
        action: "check_out",
      });

      setOpenEndDayModal(false);
      if (onRefreshTicker) {
        await onRefreshTicker();
      }
      setOpenClockOutSuccessModal(true);
    } catch (error) {
      reduxDispatch(showSnackbar({ type: AlertType.error, message: error.message, dispatch: reduxDispatch }));
    }
  };

  const handleBreakExceededUpdate = async (endTime) => {
    const currentBreakType = getBreakType();
    const isPreviousDayBreak = Boolean(previousDayBreak);

    try {
      const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser")) || {};
      const { userId: employeeId } = loggedInUser;

      if (!employeeId) {
        reduxDispatch(
          showSnackbar({ type: AlertType.error, message: "Employee ID not found", dispatch: reduxDispatch })
        );
        return;
      }

      if (isPreviousDayBreak && endTime) {
        const year = endTime.getFullYear();
        const month = String(endTime.getMonth() + 1).padStart(2, "0");
        const day = String(endTime.getDate()).padStart(2, "0");
        const hours = String(endTime.getHours()).padStart(2, "0");
        const minutes = String(endTime.getMinutes()).padStart(2, "0");
        const formattedEntryTime = `${year}-${month}-${day} ${hours}:${minutes}`;

        const actionType = previousDayBreak.type === "nursing_break" ? "nursing_break_end" : "break_end";

        await recordMissedEntry(employeeId, formattedEntryTime, actionType);
      } else {
        const action = currentBreakType === "nursing_break" ? "nursing_break_end" : "break_end";

        await clockEntry(employeeId, {
          action,
        });
      }

      if (onRefreshTicker) {
        await onRefreshTicker();
      }

      setOpenBreakExceededModal(false);
    } catch (error) {
      reduxDispatch(showSnackbar({ type: AlertType.error, message: error.message, dispatch: reduxDispatch }));
      setOpenBreakExceededModal(false);
    }
  };

  const getShiftEndTime = () => {
    const { schedule: scheduleData = {} } = tickerData || {};
    const { shiftEndTime } = scheduleData;
    return shiftEndTime ? new Date(shiftEndTime) : null;
  };

  const getClockInTime = () => {
    const { checkInTime } = tickerData || {};
    return checkInTime ? new Date(checkInTime) : null;
  };

  const getShiftDate = () => {
    const { date } = employee;
    return date ? new Date(date) : new Date();
  };

  const getMissedCheckoutData = () => {
    if (!previousDayCheckIn) return null;

    const { date, inTime, timeElapsed } = previousDayCheckIn;

    const totalHours = timeElapsed ? parseFloat((timeElapsed / 3600000).toFixed(2)) : 0;
    return {
      date: date ? moment.parseZone(date) : null,
      clockInTime: inTime ? moment.parseZone(inTime) : null,
      totalHours,
    };
  };

  const getBreakExceededModalBreakType = () => {
    if (previousDayBreak) {
      return previousDayBreak.type === "nursing_break" ? "nursing_break" : "break";
    }
    return getBreakType();
  };

  const getBreakExceededModalStartTime = () => {
    if (previousDayBreak) {
      return new Date(previousDayBreak.inTime).getTime();
    }
    if (getActiveBreak()) {
      return new Date(getActiveBreak().inTime).getTime();
    }
    return null;
  };

  const getBreakExceededModalNursingSeconds = () => {
    if (previousDayBreak) {
      return previousDayCheckIn?.totalNursingBreakTime
        ? Math.floor(previousDayCheckIn.totalNursingBreakTime / 1000)
        : 0;
    }
    return getTotalNursingTime().hours * 3600 + getTotalNursingTime().minutes * 60;
  };

  return (
    <>
      <div className="clock-in-card-v2">
        <div className="clock-in-card-v2__header">
          <div className="clock-in-card-v2__timer-section">
            <div className="clock-in-card-v2__timer-label para-body-m-medium">{getTimerLabel()}</div>
            <div className="clock-in-card-v2__timer-display">
              <p className="heading-h2-semibold">
                <span>
                  {formatTimeUnit(getActiveBreak() ? getCurrentBreakTime().hours : getTotalElapsedTime().hours)}
                </span>
                <span>:</span>
                <span>
                  {formatTimeUnit(getActiveBreak() ? getCurrentBreakTime().minutes : getTotalElapsedTime().minutes)}
                </span>
                <span className="clock-in-card-v2__timer-seconds">
                  :{formatTimeUnit(getActiveBreak() ? getCurrentBreakTime().seconds : getTotalElapsedTime().seconds)}
                </span>
              </p>
            </div>
          </div>
          <div className="clock-in-card-v2__action">
            {isClockedIn &&
              !getActiveBreak() &&
              !missedPreviousDayCheckout &&
              (isNursingBreakAllowed ? (
                <>
                  <ButtonWithDropdown
                    label="Start Break"
                    onClick={() => handleStartBreak("break")}
                    onDropdownClick={handleBreakMenuClick}
                    variant="contained"
                    color="secondary"
                    size="medium"
                    isLoading={isBreakLoading}
                    isDisabled={isBreakLoading}
                  />
                  <Menu
                    anchorEl={anchorEl}
                    open={openBreakMenu && !isBreakLoading}
                    onClose={handleBreakMenuClose}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                  >
                    <MenuItem value="Break" onClick={handleBreakSelect} disabled={isBreakLoading} />
                    <MenuItem value="Nursing" onClick={handleNursingBreakSelect} disabled={isBreakLoading} />
                  </Menu>
                </>
              ) : (
                <CustomButton
                  label="Start Break"
                  onClick={() => handleStartBreak("break")}
                  variant="contained"
                  color="secondary"
                  size="medium"
                  isLoading={isBreakLoading}
                  isDisabled={isBreakLoading}
                />
              ))}
            {(() => {
              if (getActiveBreak()) {
                return (
                  <CustomButton
                    label={getBreakType() === "break" ? "End Break" : "End Nursing Break"}
                    onClick={() => handleStartBreak()}
                    variant="contained"
                    color="primary"
                    size="large"
                    isLoading={isBreakLoading}
                    isDisabled={isBreakLoading}
                  />
                );
              }
              if (missedPreviousDayCheckout && !isClockedIn) {
                return (
                  <CustomButton
                    label="Complete Missed Clock-Out"
                    onClick={handleMissedCheckout}
                    variant="contained"
                    color="error"
                    size="medium"
                  />
                );
              }
              const getButtonLabel = () => {
                if (hasSchedule === false) {
                  return "Confirm Shift Time";
                }
                return isClockedIn ? "Clock-Out" : "Clock-In";
              };
              return (
                <CustomButton
                  label={getButtonLabel()}
                  onClick={handleClockIn}
                  variant="contained"
                  color={isClockedIn ? "error" : "primary"}
                  size={isClockedIn ? "medium" : "large"}
                  isLoading={isClockInOutLoading}
                  isDisabled={isClockInOutLoading}
                />
              );
            })()}
          </div>
        </div>
        <div className="clock-in-card-v2__footer">
          <div className="clock-in-card-v2__stats">
            {getActiveBreak() ? (
              <>
                <div className="clock-in-card-v2__stat-item">
                  <div className="clock-in-card-v2__stat-icon">
                    <WorkIcon />
                  </div>
                  <span className="clock-in-card-v2__stat-label para-body-m-medium pr-05">Time worked</span>
                  <span className="clock-in-card-v2__stat-value para-body-m-medium">
                    {getWorkTime().hours > 0 && `${getWorkTime().hours} hr `}
                    {getWorkTime().minutes} min
                  </span>
                </div>
                <div className="clock-in-card-v2__divider clock-in-card-v2__divider--vertical" />
                <div className="clock-in-card-v2__stat-item">
                  <div className="clock-in-card-v2__stat-icon">
                    {getBreakType() === "break" && isNursingBreakAllowed ? <FeederIcon /> : <BurgerIcon />}
                  </div>
                  <span className="clock-in-card-v2__stat-label para-body-m-medium pr-05">
                    {getBreakType() === "break" && isNursingBreakAllowed ? "Nursing" : "Breaks"}
                  </span>
                  {getBreakType() === "break" && isNursingBreakAllowed ? (
                    <div className="clock-in-card-v2__stat-nursing">
                      <span className="clock-in-card-v2__stat-value para-body-m-medium">
                        {getTotalNursingTime().hours} hr {getTotalNursingTime().minutes} min
                      </span>
                      <span className="clock-in-card-v2__stat-max para-body-m-medium">(max 1 hr)</span>
                    </div>
                  ) : (
                    <span className="clock-in-card-v2__stat-value para-body-m-medium">
                      {getTotalBreakTime().hours} hr {getTotalBreakTime().minutes} min
                    </span>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="clock-in-card-v2__stat-item">
                  <div className="clock-in-card-v2__stat-icon">
                    <BurgerIcon />
                  </div>
                  <span className="clock-in-card-v2__stat-label para-body-m-medium pr-05">Breaks</span>
                  <span className="clock-in-card-v2__stat-value para-body-m-medium">
                    {getTotalBreakTime().hours} hr {getTotalBreakTime().minutes} min
                  </span>
                </div>
                {isNursingBreakAllowed && (
                  <>
                    <div className="clock-in-card-v2__divider clock-in-card-v2__divider--vertical" />
                    <div className="clock-in-card-v2__stat-item">
                      <div className="clock-in-card-v2__stat-icon">
                        <FeederIcon />
                      </div>
                      <span className="clock-in-card-v2__stat-label para-body-m-medium pr-05">Nursing</span>
                      <div className="clock-in-card-v2__stat-nursing">
                        <span className="clock-in-card-v2__stat-value para-body-m-medium">
                          {getTotalNursingTime().hours} hr {getTotalNursingTime().minutes} min
                        </span>
                        <span className="clock-in-card-v2__stat-max para-body-m-medium">(max 1 hr)</span>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
          <div className="clock-in-card-v2__timesheet-link para-body-m-medium" onClick={handleViewTimesheet}>
            View Timesheet
          </div>
        </div>
      </div>
      <SelectShiftModal
        open={openSelectShift}
        onClose={handleSelectShiftClose}
        onConfirm={handleSelectShiftConfirm}
        timeoffCountry={timeoffCountry}
      />
      <BreakTimeExceededModal
        open={openBreakExceededModal}
        onClose={handleBreakExceededModalClose}
        onUpdate={handleBreakExceededUpdate}
        breakType={getBreakExceededModalBreakType()}
        breakStartTime={getBreakExceededModalStartTime()}
        totalNursingBreakSeconds={getBreakExceededModalNursingSeconds()}
        shiftEndTime={getShiftEndTime()?.getTime() || null}
      />
      <EndDayModal
        open={openEndDayModal}
        onClose={handleEndDayModalClose}
        onConfirm={handleEndDayConfirm}
        daySummary={getDaySummary(modalTickerData)}
        isLoading={isLoadingModalData}
      />
      <MissedClockOutModal
        open={openMissedClockOutModal}
        onClose={handleMissedClockOutModalClose}
        onSave={handleMissedClockOutSave}
        clockInTime={getMissedCheckoutData()?.clockInTime || getClockInTime()}
        shiftDate={getMissedCheckoutData()?.date || getShiftDate()}
        shiftEndTime={getShiftEndTime()}
      />
      <ClockOutSuccessModal
        open={openClockOutSuccessModal}
        onClose={handleClockOutSuccessModalClose}
        onViewTimesheet={handleViewTimesheetFromSuccess}
        daySummary={cachedDaySummary || getDaySummary(modalTickerData)}
      />
    </>
  );
}

ClockInCardV2.propTypes = {
  tickerData: PropTypes.shape({}),
  onShiftConfirm: PropTypes.func,
  onAttendanceUpdate: PropTypes.func,
  onRefreshTicker: PropTypes.func,
};

ClockInCardV2.defaultProps = {
  tickerData: null,
  onShiftConfirm: null,
  onAttendanceUpdate: null,
  onRefreshTicker: null,
};

export default ClockInCardV2;
